import { keepPreviousData, useInfiniteQuery, useQuery, useMutation } from '@tanstack/vue-query';
import type { MaybeRef } from 'vue';
import { computed, toValue } from 'vue';
import type { PropertyOffer, PropertyDetailed, Property, NearbyLocation } from '../../types/properties';
import { queries } from '../../keys';
import type { SuccessResponse } from '../../types/response';
import { splitSearchQueryForResidentials } from '@/helpers/catalogueSearchParams';

type SuccessResponseExtended<T> = Omit<SuccessResponse<T>, 'pagination'> & {
  total: number;
  execution_time: string;
};

export const usePropertyOffer = (hash: PropertyOffer['id']) => {
  const { fetch } = useAPIFetch();

  return useQuery({
    ...queries.properties.offer(hash),
    queryFn: () =>
      fetch<SuccessResponseExtended<PropertyOffer>>({
        method: 'GET',
        url: `catalogue/offers/${hash}`,
      }),
    select: (data) => data.data,
    retry: false,
  });
};

export const useGetProperty = (id: MaybeRef<Property['id']>, enabled: MaybeRef<boolean> = true) => {
  const authStore = useAuthStore();
  return useQuery({
    queryKey: computed(() => queries.properties.offer(toValue(id) as Property['id']).queryKey),
    queryFn: async () => {
      const rawId = toValue(id);
      try {
        // Postgres v2 detail endpoint. Server-side merges in the Geniemap
        // gap fields (br_prices, nearby_locations, albums, type, i18n
        // titles) so the response shape matches the legacy
        // /api/catalogue-proxy/residentials/:id payload the page consumes.
        // Sends Authorization so server-side getAccessLevel returns
        // registered/verified for logged-in agents — without it the response
        // gets guest-redacted (apartments stripped, AMs masked, etc.) and
        // tabs like Availability disappear.
        //
        // SECURITY: the header is gated to import.meta.client. This page
        // (/properties/<id>) is SWR-cached in prod — if this queryFn ever
        // ran during SSR (today vue-query doesn't await it server-side, but
        // adding suspense/onServerPrefetch/the vue-query-nuxt module would
        // change that), sending the token would bake an agent's full authed
        // data into the shared cache and leak it to every guest/crawler.
        // Client-only header = the SSR render is always the guest view
        // (user-independent, cache-safe); agents still get full data because
        // the query runs client-side after hydration. Mirrors the explicit
        // public-SSR + client-reveal split on /projects/<dev>/<slug>.
        const headers = import.meta.client && authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {};
        return await $fetch<SuccessResponse<PropertyDetailed>>(`/api/properties/v2/${rawId}`, {
          headers,
        });
      } catch (error: any) {
        const status = error?.response?.status ?? error?.statusCode;
        if (status === 422 || status === 404 || status === 500 || status === 502) {
          return null;
        }
        return null;
      }
    },
    select: (data) => {
      try {
        if (!data) return null;
        if (data?.data) {
          return { ...data.data, isExclusive: data.data.exclusive?.isExclusive === true };
        }
        return data.data;
      } catch (error) {
        return null;
      }
    },
    enabled: computed(() => toValue(enabled) && !!toValue(id)),
    retry: false,
  });
};
export const useFavoriteProperties = () => {
  const authStore = useAuthStore();

  return useQuery({
    queryKey: ['properties', 'favorites'],
    queryFn: async () => {
      const favoritesResponse = await $fetch<{
        success: boolean;
        data: { favorited_ids: string[] };
      }>('/api/favorites/all', {
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });

      const favoriteIds = favoritesResponse?.data?.favorited_ids || [];

      if (favoriteIds.length === 0) {
        return [];
      }

      // Single Postgres-backed batch fetch keyed on Geniemap complex IDs.
      const listResponse = await $fetch<{
        success: boolean;
        data: { items: Property[]; total: number };
      }>('/api/properties/v2/list', {
        params: {
          ids: favoriteIds.join(','),
          limit: Math.min(100, Math.max(1, favoriteIds.length)),
        },
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });

      const items = listResponse?.data?.items ?? [];

      return items.map((property: Property) => ({
        ...property,
        isExclusive: property.exclusive?.isExclusive === true,
      }));
    },
    enabled: computed(() => !!authStore.token),
    staleTime: 300000,
    gcTime: 600000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useInfiniteProperties = ({
  query,
  queryKey,
  enabled,
}: {
  query?: Ref<any>;
  queryKey?: MaybeRef<any>;
  enabled?: MaybeRef<boolean>;
} = {}) => {
  const authStore = useAuthStore();
  const queryOpts: Parameters<typeof useInfiniteQuery>[0] = {
    ...queries.properties.all(queryKey),
    queryFn: async ({ pageParam = 1 }) => {
      const queryValue = query?.value || {};
      // `favorites_only` and `developer_vault_only` are client-side filters
      // (we filter the response in `pages/explore.vue`) — never forward them
      // to the residentials API. `exclusive` becomes `is_exclusive=1`.
      const { exclusive, query: searchQuery, favorites_only, developer_vault_only, ...restParams } = queryValue;

      const hasDevFilter = Array.isArray(queryValue.developer_names) && queryValue.developer_names.length > 0;
      const searchParts = splitSearchQueryForResidentials(searchQuery, hasDevFilter);

      // Translate FE-side price filter keys (price_min / price_max) to the
      // server's expected names (min_price / max_price). The naming
      // disagreement was silently dropping the price filter entirely —
      // server saw nothing matching its expected keys and skipped both
      // bounds.
      const { price_min: priceMinFilter, price_max: priceMaxFilter, ...restWithoutPrice } = restParams;

      const finalParams = {
        // restParams forwards every typed `Filters` field that survived
        // `useParseFilters`'s empty-value pruning. The Postgres v2 endpoint
        // ignores any params it doesn't recognize, so passing the legacy
        // catalogue-proxy fields through is safe.
        ...restWithoutPrice,
        ...(priceMinFilter ? { min_price: priceMinFilter } : {}),
        ...(priceMaxFilter ? { max_price: priceMaxFilter } : {}),
        ...(searchParts.title ? { search: searchParts.title } : {}),
        ...(searchParts.developer_names?.length ? { developer_names: searchParts.developer_names } : {}),
        ...(exclusive ? { is_exclusive: 1 } : {}),
        ...{
          top_left_lat: queryValue.ignoreCoordinates ? undefined : queryValue?.top_left_lat,
          top_left_lon: queryValue.ignoreCoordinates ? undefined : queryValue?.top_left_lon,
          bottom_right_lat: queryValue.ignoreCoordinates ? undefined : queryValue?.bottom_right_lat,
          bottom_right_lon: queryValue.ignoreCoordinates ? undefined : queryValue?.bottom_right_lon,
        },
        page: pageParam,
      };

      if (!finalParams.order_by) {
        finalParams.order_by = 'updated_at';
        finalParams.order_way = 'desc';
      }

      const res = await $fetch<{
        success: boolean;
        data: { items: Property[]; total: number; page: number; limit: number };
      }>('/api/properties/v2/list', {
        params: finalParams,
        headers: authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {},
      });
      // Adapt the v2 envelope `{ data: { items, total } }` to the flat shape
      // (`{ data: Property[], total }`) the existing list/map consumers rely on.
      return {
        success: res.success,
        data: res.data.items,
        total: res.data.total,
      } as SuccessResponseExtended<Property[]>;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      const totalProperties = pages.reduce((acc, page) => acc + page.data.length, 0);
      return totalProperties < lastPage.total ? pages.length + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    staleTime: 300000,
    gcTime: 600000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    select: (data) => {
      if (data?.pages) {
        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            data: page.data.map((property: Property) => ({
              ...property,
              isExclusive: property.exclusive?.isExclusive === true,
            })),
          })),
        };
      }
      return data;
    },
    retry: false,
  };

  if (enabled !== undefined) {
    queryOpts.enabled = computed(() => toValue(enabled));
  }

  return useInfiniteQuery(queryOpts);
};

export const useMapProperties = ({
  query,
  queryKey,
}: {
  query?: MaybeRef<any>;
  queryKey?: MaybeRef<any>;
} = {}) => {
  const authStore = useAuthStore();
  return useQuery({
    ...queries.properties.map(queryKey),
    queryFn: async ({ signal }) => {
      const queryValue = query?.value || {};
      const {
        exclusive,
        query: searchQuery,
        favorites_only,
        developer_vault_only,
        ignoreCoordinates,
        top_left_lat,
        top_left_lon,
        bottom_right_lat,
        bottom_right_lon,
        zoom,
        ...restParams
      } = queryValue;

      const hasDevFilter = Array.isArray(queryValue.developer_names) && queryValue.developer_names.length > 0;
      const searchParts = splitSearchQueryForResidentials(searchQuery, hasDevFilter);

      const hasViewportBounds =
        !ignoreCoordinates && top_left_lat && top_left_lon && bottom_right_lat && bottom_right_lon;
      const currentZoom = zoom ? Number(zoom) : 10;
      const needsClustering = currentZoom < 13;
      const limit = needsClustering ? 1000 : 200;

      // Same FE→server price-key translation as useInfiniteProperties above.
      const { price_min: priceMinFilter, price_max: priceMaxFilter, ...restWithoutPrice } = restParams;

      const res = await $fetch<{
        success: boolean;
        data: { items: Property[]; total: number; page: number; limit: number };
      }>('/api/properties/v2/list', {
        params: {
          ...restWithoutPrice,
          ...(priceMinFilter ? { min_price: priceMinFilter } : {}),
          ...(priceMaxFilter ? { max_price: priceMaxFilter } : {}),
          ...(searchParts.title ? { search: searchParts.title } : {}),
          ...(searchParts.developer_names?.length ? { developer_names: searchParts.developer_names } : {}),
          ...(exclusive ? { is_exclusive: 1 } : {}),
          // v2 (Postgres) doesn't support viewport-bounds filtering yet, so
          // these are forwarded but will be ignored server-side; the explore
          // page already falls back to list data when bounds are present.
          ...(hasViewportBounds
            ? {
                top_left_lat,
                top_left_lon,
                bottom_right_lat,
                bottom_right_lon,
              }
            : {}),
          page: 1,
          limit,
          order_by: 'updated_at',
          order_way: 'desc',
        },
        signal,
        headers: authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {},
      });
      // Adapt v2 envelope `{ data: { items } }` to the flat `{ data: Property[] }`
      // shape that downstream `select()` expects.
      return {
        success: res.success,
        data: res.data.items,
      } as SuccessResponse<Property[]>;
    },
    select: (data: SuccessResponse<Property[]>) => {
      if (data?.data) {
        return data.data.map((property: Property) => ({
          ...property,
          isExclusive: property.exclusive?.isExclusive === true,
        }));
      }
      return data?.data || [];
    },
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 300000,
    gcTime: 600000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Lightweight "all matching pins" for the explore map. Unlike
// useMapProperties (capped at 100/1000 full Property rows), this hits the
// dedicated /map-pins endpoint which returns minimal markers for EVERY
// project matching the active filters — so the map shows all ~2.5k projects,
// not just the first page. Filters are forwarded identically.
export const useMapPins = ({
  query,
  queryKey,
}: {
  query?: MaybeRef<any>;
  queryKey?: MaybeRef<any>;
} = {}) => {
  const authStore = useAuthStore();
  return useQuery({
    queryKey: computed(() => ['properties', 'map-pins', unref(queryKey) ?? unref(query) ?? {}]),
    queryFn: async ({ signal }) => {
      const queryValue = (query as any)?.value || {};
      const {
        exclusive,
        query: searchQuery,
        favorites_only,
        developer_vault_only,
        ignoreCoordinates,
        top_left_lat,
        top_left_lon,
        bottom_right_lat,
        bottom_right_lon,
        zoom,
        page,
        per_page,
        limit,
        order_by,
        order_way,
        price_min: priceMinFilter,
        price_max: priceMaxFilter,
        ...restParams
      } = queryValue;

      const hasDevFilter = Array.isArray(queryValue.developer_names) && queryValue.developer_names.length > 0;
      const searchParts = splitSearchQueryForResidentials(searchQuery, hasDevFilter);

      const res = await $fetch<{ success: boolean; data: { items: Property[]; total: number } }>(
        '/api/properties/v2/map-pins',
        {
          params: {
            ...restParams,
            ...(priceMinFilter ? { min_price: priceMinFilter } : {}),
            ...(priceMaxFilter ? { max_price: priceMaxFilter } : {}),
            ...(searchParts.title ? { search: searchParts.title } : {}),
            ...(searchParts.developer_names?.length ? { developer_names: searchParts.developer_names } : {}),
            ...(exclusive ? { is_exclusive: 1 } : {}),
          },
          signal,
          headers: authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {},
        },
      );
      return { success: res.success, data: res.data.items } as SuccessResponse<Property[]>;
    },
    select: (data: SuccessResponse<Property[]>) => data?.data || [],
    // Client-only (ID-165): the Google map only renders in the browser. Run during SSR, the query either finished
    // and baked every pin (~2.5k, +1 MB) into the HTML payload, or was abandoned mid-flight on the server.
    enabled: import.meta.client,
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 300000,
    gcTime: 600000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useGetBrochureLink = () => {
  const { fetch } = useAPIPPFetch();

  return useMutation({
    mutationFn: (projectId: string) =>
      fetch<SuccessResponse<{ is_unique: boolean }>>({
        method: 'POST',
        url: `catalogue/link`,
        data: { project_id: projectId },
      }),
  });
};

export const useNearbyLocations = (
  propertyId: MaybeRef<string | undefined | null>,
  coordinates?: MaybeRef<{ latitude: number; longitude: number } | null | undefined>,
) => {
  const queryKey = computed(() => {
    const c = coordinates ? toValue(coordinates) : null;
    const lat = c && Number.isFinite(c.latitude) ? c.latitude : null;
    const lng = c && Number.isFinite(c.longitude) ? c.longitude : null;
    return ['nearby-locations', toValue(propertyId), lat, lng] as const;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const c = coordinates ? toValue(coordinates) : null;
      const id = toValue(propertyId);
      const base = `/api/properties/${id}/nearby-locations`;
      const url =
        c && Number.isFinite(c.latitude) && Number.isFinite(c.longitude)
          ? `${base}?lat=${encodeURIComponent(c.latitude)}&lng=${encodeURIComponent(c.longitude)}`
          : base;
      try {
        const res = await $fetch<SuccessResponse<NearbyLocation[]>>(url);
        return res;
      } catch {
        return { success: true, data: [] as NearbyLocation[] };
      }
    },
    select: (data) => data?.data ?? [],
    retry: false,
    // Enable when we have EITHER a propertyId OR explicit coordinates —
    // the server endpoint accepts lat/lng as a query-string override and
    // doesn't need the id when coords are passed.
    enabled: computed(() => {
      const id = toValue(propertyId);
      const c = coordinates ? toValue(coordinates) : null;
      return Boolean(id) || (c != null && Number.isFinite(c.latitude) && Number.isFinite(c.longitude));
    }),
  });
};
